import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Paperclip, CheckCircle2, XCircle, Banknote } from 'lucide-react';
import { expenses as expensesApi, type Expense, type ExpenseEvent } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { STATUS_META } from '../../lib/expenseStatus';

const EVENT_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  approved_by_approver: 'Approved by manager',
  rejected_by_approver: 'Rejected by manager',
  approved_by_finance: 'Approved by finance',
  rejected_by_finance: 'Rejected by finance',
  paid: 'Marked as paid',
};

export default function ExpenseDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [expense, setExpense] = useState<Expense | null>(null);
  const [events, setEvents] = useState<ExpenseEvent[]>([]);
  const [canActAsApprover, setCanActAsApprover] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { expense, events, canActAsApprover } = await expensesApi.get(id!);
      setExpense(expense);
      setEvents(events);
      setCanActAsApprover(canActAsApprover);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (fn: () => Promise<unknown>) => {
    setActionLoading(true);
    setError('');
    try {
      await fn();
      setComment('');
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" /></div>;
  if (error && !expense) return <div className="card text-red-600 text-center py-10">{error}</div>;
  if (!expense || !user) return null;

  const meta = STATUS_META[expense.status];
  const isApprover = canActAsApprover && expense.status === 'pending_approver';
  const isFinance = !!user.is_finance_approver;
  const canFinanceAct = isFinance && expense.status === 'pending_finance';
  const canMarkPaid = isFinance && expense.status === 'approved';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/expenses/mine" className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Expense Detail</h1>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

      <div className="card space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums">{expense.amount.toFixed(2)}</p>
            <p className="text-gray-500 text-sm">{expense.description || 'No description'}</p>
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${meta.className}`}>{meta.label}</span>
        </div>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div><dt className="text-gray-400">Expense date</dt><dd className="text-gray-900 font-medium">{expense.expense_date}</dd></div>
          <div><dt className="text-gray-400">Fiscal year</dt><dd className="text-gray-900 font-medium">{expense.fiscal_year}</dd></div>
        </dl>
        {expense.receipt_r2_key && (
          <a href={expensesApi.receiptUrl(expense.id)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-green-600 hover:underline w-fit">
            <Paperclip className="w-4 h-4" /> View receipt
          </a>
        )}
      </div>

      {(isApprover || canFinanceAct) && (
        <div className="card space-y-3">
          <h2 className="font-semibold text-gray-900">{isApprover ? 'Your approval' : 'Finance review'}</h2>
          <textarea
            className="input-field resize-none"
            placeholder="Comment (required to reject)"
            rows={2}
            value={comment}
            onChange={e => setComment(e.target.value)}
          />
          <div className="flex gap-3">
            <button
              onClick={() => runAction(() => isApprover ? expensesApi.approve(expense.id, comment || undefined) : expensesApi.financeApprove(expense.id, comment || undefined))}
              className="btn-primary flex items-center gap-2 flex-1 justify-center"
              disabled={actionLoading}
            >
              <CheckCircle2 className="w-4 h-4" /> Approve
            </button>
            <button
              onClick={() => {
                if (!comment) { setError('A comment is required to reject'); return; }
                runAction(() => isApprover ? expensesApi.reject(expense.id, comment) : expensesApi.financeReject(expense.id, comment));
              }}
              className="btn-danger flex items-center gap-2 flex-1 justify-center"
              disabled={actionLoading}
            >
              <XCircle className="w-4 h-4" /> Reject
            </button>
          </div>
        </div>
      )}

      {canMarkPaid && (
        <div className="card">
          <button
            onClick={() => runAction(() => expensesApi.markPaid(expense.id))}
            className="btn-primary flex items-center gap-2 w-full justify-center"
            disabled={actionLoading}
          >
            <Banknote className="w-4 h-4" /> Mark as Paid
          </button>
        </div>
      )}

      <div className="card space-y-4">
        <h2 className="font-semibold text-gray-900">History</h2>
        <div className="space-y-3">
          {events.map(ev => (
            <div key={ev.id} className="flex items-start gap-3 text-sm">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">{EVENT_LABELS[ev.event_type] || ev.event_type}</p>
                <p className="text-gray-400 text-xs">{new Date(ev.created_at * 1000).toLocaleString()}</p>
                {ev.comment && <p className="text-gray-600 mt-1">{ev.comment}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
